import { bootstrapApplication } from '@angular/platform-browser';
import {
  PublicClientApplication,
  type IPublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser';
import {
  MSAL_INSTANCE,
  MsalService,
  MsalBroadcastService,
} from '@azure/msal-angular';

import type { AzureConfig } from './auth.config';
import { environment } from './environments/environment';

// Expose redirect result for AppComponent to consume after bootstrap
declare global {
  interface Window {
    __msalRedirectResult?: AuthenticationResult | null;
  }
}

async function bootstrap(): Promise<void> {
  const apiUrl = environment.apiUrl.replace(/\/+$/, ''); // strip trailing slash
  let azureConfig: AzureConfig | null = null;

  // 1. Try sessionStorage first (instant — covers return visits)
  const cached = sessionStorage.getItem('azure_config');
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed.clientId && parsed.tenantId) {
        azureConfig = parsed;
      }
    } catch {
      sessionStorage.removeItem('azure_config');
    }
  }

  // 2. If no cache, fetch from tenant API (native fetch — no Angular, no interceptors)
  if (!azureConfig) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${apiUrl}/api/v3/tenant/`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const tenant = await response.json();
        if (tenant.azure_client_id && tenant.azure_tenant_id) {
          azureConfig = {
            clientId: tenant.azure_client_id,
            tenantId: tenant.azure_tenant_id,
            redirectUri: tenant.redirect_uri || window.location.origin,
            postLogoutRedirectUri:
              tenant.post_logout_redirect_uri || window.location.origin,
          };
          sessionStorage.setItem('azure_config', JSON.stringify(azureConfig));
        }
      }
    } catch (e) {
      console.warn('Pre-bootstrap tenant fetch failed:', e);
    }
  }

  // 3. Create MSAL PublicClientApplication (always — even with empty config for DI)
  const pca: IPublicClientApplication = new PublicClientApplication({
    auth: {
      clientId: azureConfig?.clientId || '',
      authority: azureConfig?.tenantId
        ? `https://login.microsoftonline.com/${azureConfig.tenantId}`
        : '',
      redirectUri: azureConfig?.redirectUri || '',
      postLogoutRedirectUri: azureConfig?.postLogoutRedirectUri || '',
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: true,
    },
  });

  await pca.initialize();

  // 4. Handle OAuth redirect BEFORE Angular boots (only if config is valid)
  if (azureConfig?.clientId) {
    try {
      const redirectResult = await pca.handleRedirectPromise();
      if (redirectResult) {
        window.__msalRedirectResult = redirectResult;
      }
    } catch (e) {
      console.error('MSAL redirect handling error:', e);
    }
  }

  // 5. Bootstrap Angular with fully-configured MSAL instance
  const { appConfig } = await import('./app/app.config');
  const { AppComponent } = await import('./app/app.component');

  await bootstrapApplication(AppComponent, {
    providers: [
      ...appConfig.providers,
      { provide: MSAL_INSTANCE, useValue: pca },
      MsalService,
      MsalBroadcastService,
    ],
  });
}

bootstrap().catch((err) => console.error('Bootstrap failed:', err));
